import React, { useState } from 'react';
import { Notification, NotificationStatus, Tool, Employee, NotificationType } from '../types';
import { Check, X, Clock, User, ExternalLink, ShieldAlert, FileQuestion, GraduationCap, BookOpen, ChevronRight, FileCheck, Send, AlertTriangle, MessageCircle, Filter, CheckCircle, Bell } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';

interface NotificationCenterViewProps {
  notifications: Notification[];
  onNotificationsChange: (updated: Notification[]) => void;
  onUpdateNotification: (notif: Notification) => Promise<void>;
  tools?: Tool[];
  employees?: Employee[];
  onAddNotification?: (notif: Notification) => void;
  onAccessTaskClosed?: (notif: Notification, newStatus: string) => void;
}

const CLOSED_STATUSES = ['DONE', 'ABORTED', 'REJECTED', 'APPROVED', 'ACKNOWLEDGED', 'FORWARDED'];
const isAccessChangeRequest = (notif: Notification) =>
  notif.type === 'ACCESS_REQUEST' && notif.requestKind === 'change';

export const NotificationCenterView: React.FC<NotificationCenterViewProps> = ({ 
    notifications, 
    onNotificationsChange,
    onUpdateNotification,
    tools = [], 
    employees = [],
    onAddNotification,
    onAccessTaskClosed,
}) => {
  const [filter, setFilter] = useState<'ALL' | 'PENDING'>('PENDING');
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // All History filters (only used when filter === 'ALL')
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterToolId, setFilterToolId] = useState('');
  const [filterEmployeeId, setFilterEmployeeId] = useState('');
  
  // State for Procurement Checklist
  const [checklist, setChecklist] = useState({
      budget: false,
      security: false,
      tech: false
  });
  const [adminNotes, setAdminNotes] = useState('');

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'PENDING') {
      return n.status === 'PENDING' || n.status === 'CLARIFICATION_NEEDED';
    }
    // All History + optional filters
    if (filterDateFrom) {
      const created = n.createdAt ? new Date(n.createdAt).toISOString().slice(0, 10) : '';
      if (created < filterDateFrom) return false;
    }
    if (filterDateTo) {
      const created = n.createdAt ? new Date(n.createdAt).toISOString().slice(0, 10) : '';
      if (created > filterDateTo) return false;
    }
    if (filterToolId && n.toolId !== filterToolId) return false;
    if (filterEmployeeId && n.requesterId !== filterEmployeeId) return false;
    return true;
  });

  const openReviewModal = (notif: Notification) => {
      setSelectedNotification(notif);
      setChecklist({ budget: false, security: false, tech: false });
      setAdminNotes('');
      setRejectReason('');
  };

  const handleSimpleAction = async (notif: Notification, status: NotificationStatus) => {
    const updated = { ...notif, status };
    await onUpdateNotification(updated);
    if (CLOSED_STATUSES.includes(status)) onAccessTaskClosed?.(notif, status);
  };

  const handleApproveProcurement = async () => {
      if (!selectedNotification) return;
      const updatedNotif: Notification = {
          ...selectedNotification,
          status: 'APPROVED',
          reviewData: {
              checklist: {
                  budgetApproved: checklist.budget,
                  securityChecked: checklist.security,
                  techReview: checklist.tech
              },
              adminNotes: adminNotes
          }
      };
      await onUpdateNotification(updatedNotif);
      onAccessTaskClosed?.(selectedNotification, 'APPROVED');
      setSelectedNotification(null);
  };

  const handleRequestInfo = async () => {
      if (!selectedNotification || !adminNotes.trim()) return;
      const newHistoryItem = {
          senderId: 'admin',
          senderName: 'Admin',
          role: 'admin' as const,
          message: adminNotes,
          date: new Date().toISOString()
      };
      const updatedNotif: Notification = {
          ...selectedNotification,
          status: 'CLARIFICATION_NEEDED',
          history: [...(selectedNotification.history || []), newHistoryItem]
      };
      await onUpdateNotification(updatedNotif);
      setSelectedNotification(null);
  };

  const handleValidateAndForward = async () => {
      if (!selectedNotification || !onAddNotification) return;
      const updatedNotif: Notification = {
          ...selectedNotification,
          status: 'FORWARDED',
          reviewData: {
              adminNotes: adminNotes,
              forwardedToOwnerId: getToolOwnerId(selectedNotification)
          }
      };
      await onUpdateNotification(updatedNotif);
      const ownerId = getToolOwnerId(selectedNotification);
      if (ownerId) {
          const newNotif: Notification = {
              id: crypto.randomUUID(),
              type: 'OWNER_TASK',
              status: 'PENDING',
              requesterId: 'admin',
              requesterName: 'Admin System',
              createdAt: new Date().toISOString(),
              toolId: selectedNotification.toolId,
              toolName: selectedNotification.toolName,
              // Reuse fields for context
              accessReason: selectedNotification.type === 'ACCESS_REQUEST' 
                ? `Please provision access for ${selectedNotification.requesterName}. Reason: ${selectedNotification.accessReason}` 
                : undefined,
              trainingNeeds: selectedNotification.type === 'TRAINING_REQUEST'
                ? `Please arrange training for ${selectedNotification.requesterName}. Needs: ${selectedNotification.trainingNeeds}`
                : undefined
          };
          onAddNotification(newNotif);
      }

      onAccessTaskClosed?.(selectedNotification, 'FORWARDED');
      setSelectedNotification(null);
  };

  const handleReject = async () => {
      if (!selectedNotification || !rejectReason.trim()) return;
      const updatedNotif: Notification = {
          ...selectedNotification,
          status: 'REJECTED',
          reviewData: {
              adminNotes: rejectReason
          }
      };
      await onUpdateNotification(updatedNotif);
      onAccessTaskClosed?.(selectedNotification, 'REJECTED');
      setSelectedNotification(null);
  };

  const getToolOwnerId = (notif: Notification) => {
      const tool = tools.find(t => t.id === notif.toolId);
      return tool?.powerUserId;
  };
  
  const getToolOwnerName = (notif: Notification) => {
      const ownerId = getToolOwnerId(notif);
      if (!ownerId) return 'Unassigned';
      return employees.find(e => e.id === ownerId)?.name || 'Unknown';
  };

  const getTypeColor = (type: string) => {
      switch(type) {
          case 'ACCESS_REQUEST': return 'bg-blue-100 text-blue-600';
          case 'TRAINING_COMPLETED': return 'bg-emerald-100 text-emerald-600';
          case 'TRAINING_REQUEST': return 'bg-amber-100 text-amber-600';
          case 'OWNER_TASK': return 'bg-indigo-100 text-indigo-600';
          default: return 'bg-purple-100 text-purple-600';
      }
  }

  const getTypeIcon = (type: string) => {
      switch(type) {
          case 'ACCESS_REQUEST': return <User size={20} />;
          case 'TRAINING_COMPLETED': return <Check size={20} />;
          case 'TRAINING_REQUEST': return <GraduationCap size={20} />;
          case 'OWNER_TASK': return <Check size={20} />;
          default: return <FileQuestion size={20} />;
      }
  }

  const getTypeLabel = (type: string, notif?: Notification) => {
      switch(type) {
          case 'ACCESS_REQUEST': return notif?.requestKind === 'change'
            ? 'Access Change Request'
            : 'Access Request';
          case 'TRAINING_COMPLETED': return 'Training Update';
          case 'TRAINING_REQUEST': return 'Training Needed';
          case 'OWNER_TASK': return 'Task for You';
          default: return 'Procurement Request';
      }
  }

  return (
    <div className="space-y-6 animate-fade-in relative">
      <div className="flex justify-end items-center">
        <div className="bg-white p-1 rounded-lg border border-slate-200 flex">
            <button 
                onClick={() => setFilter('PENDING')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === 'PENDING' ? 'bg-toggle text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
                Pending
            </button>
            <button 
                onClick={() => setFilter('ALL')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === 'ALL' ? 'bg-toggle text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
                All History
            </button>
        </div>
      </div>

      {/* All History filters */}
      {filter === 'ALL' && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
            <Filter size={18} />
            Filter by
          </div>
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">From date</label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
                className="w-full min-w-[140px] p-2 border border-slate-300 rounded-lg bg-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">To date</label>
              <input
                type="date"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
                className="w-full min-w-[140px] p-2 border border-slate-300 rounded-lg bg-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tool</label>
              <select
                value={filterToolId}
                onChange={e => setFilterToolId(e.target.value)}
                className="w-full min-w-[160px] p-2 border border-slate-300 rounded-lg bg-white text-sm"
              >
                <option value="">All tools</option>
                {tools.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Employee</label>
              <select
                value={filterEmployeeId}
                onChange={e => setFilterEmployeeId(e.target.value)}
                className="w-full min-w-[160px] p-2 border border-slate-300 rounded-lg bg-white text-sm"
              >
                <option value="">All employees</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); setFilterToolId(''); setFilterEmployeeId(''); }}
              className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {filteredNotifications.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-slate-300">
              <EmptyState
                icon={filter === 'PENDING' ? <CheckCircle size={40} /> : <Bell size={40} />}
                title={filter === 'PENDING' ? 'Inbox zero' : 'No notifications yet'}
                subtitle="No notifications to review right now. Requests, alerts, and updates will appear here."
                positive={filter === 'PENDING'}
              />
            </div>
        ) : (
            filteredNotifications.map(notif => (
                <div key={notif.id} className={`bg-white rounded-xl p-6 border transition-all ${notif.status === 'PENDING' || notif.status === 'CLARIFICATION_NEEDED' ? 'border-indigo-200 shadow-sm' : 'border-slate-200 opacity-75'}`}>
                    <div className="flex justify-between items-start">
                        <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getTypeColor(notif.type)}`}>
                                {getTypeIcon(notif.type)}
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-bold text-slate-800">
                                        {getTypeLabel(notif.type, notif)}
                                    </span>
                                    {isAccessChangeRequest(notif) && (
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase bg-indigo-100 text-indigo-700">
                                            Change
                                        </span>
                                    )}
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                                        notif.status === 'PENDING' ? 'bg-amber-100 text-amber-700' : 
                                        notif.status === 'CLARIFICATION_NEEDED' ? 'bg-purple-100 text-purple-700' :
                                        notif.status === 'APPROVED' || notif.status === 'ACKNOWLEDGED' || notif.status === 'FORWARDED' || notif.status === 'DONE' ? 'bg-green-100 text-green-700' :
                                        notif.status === 'ABORTED' ? 'bg-slate-100 text-slate-600' : 'bg-red-100 text-red-700'
                                    }`}>
                                        {notif.status === 'ACKNOWLEDGED' ? 'Done' : notif.status === 'DONE' ? 'Done' : notif.status === 'ABORTED' ? 'Aborted' : notif.status === 'CLARIFICATION_NEEDED' ? 'Awaiting Reply' : notif.status}
                                    </span>
                                    <span className="text-xs text-slate-400 flex items-center gap-1">
                                        <Clock size={12} /> {new Date(notif.createdAt).toLocaleDateString()}
                                    </span>
                                </div>
                                <p className="text-sm text-slate-600 mb-2">
                                    <span className="font-semibold">{notif.requesterName}</span> 
                                    {notif.type === 'ACCESS_REQUEST' && (notif.requestKind === 'change' ? ' requests an access change for ' : ' wants access to ')} 
                                    {notif.type === 'NEW_TOOL_REQUEST' && ' is requesting '}
                                    {notif.type === 'TRAINING_COMPLETED' && ' has completed training for '}
                                    {notif.type === 'TRAINING_REQUEST' && ' is requesting training on '}
                                    {notif.type === 'OWNER_TASK' && ' (Admin) assigned you a task: '}
                                    <span className="font-semibold text-indigo-700">
                                        {notif.type === 'NEW_TOOL_REQUEST' ? notif.newToolData?.name : notif.toolName}
                                    </span>
                                </p>
                                
                                {/* Details Box */}
                                {(notif.type !== 'TRAINING_COMPLETED') && (
                                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 text-sm space-y-2 mt-2 max-w-2xl text-slate-900">
                                        {notif.type === 'ACCESS_REQUEST' && (
                                            <>
                                                <p><span className="font-bold text-black">{notif.requestKind === 'change' ? 'Requested change:' : 'Reason:'}</span> {notif.accessReason}</p>
                                                <p><span className="font-bold text-black">Needed By:</span> {notif.accessNeededBy}</p>
                                            </>
                                        )}
                                        {(notif.type === 'TRAINING_REQUEST' || notif.type === 'OWNER_TASK') && (notif.trainingNeeds || notif.accessReason) && (
                                            <>
                                                <p className="flex gap-2 items-start">
                                                    <span className="font-bold text-black flex-shrink-0">Details:</span> 
                                                    <span>{notif.trainingNeeds || notif.accessReason}</span>
                                                </p>
                                            </>
                                        )}
                                        {notif.type === 'NEW_TOOL_REQUEST' && notif.newToolData && (
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                                <p className="col-span-2"><span className="font-bold text-black">Purpose:</span> {notif.newToolData.purpose}</p>
                                                <p><span className="font-bold text-black">Price:</span> {notif.newToolData.priceEstimation}</p>
                                                <p><span className="font-bold text-black">Alternatives:</span> {notif.newToolData.alternatives}</p>
                                                <p className="col-span-2 flex items-center gap-2">
                                                    <span className="font-bold text-black">GDPR:</span> 
                                                    {notif.newToolData.gdprCompliant ? (
                                                        <span className="text-green-700 flex items-center gap-1 text-xs font-bold"><ShieldAlert size={12} /> Compliant</span>
                                                    ) : (
                                                        <span className="text-red-700 text-xs font-bold">Not Confirmed</span>
                                                    )}
                                                </p>
                                                <a href={notif.newToolData.termsUrl} target="_blank" rel="noreferrer" className="text-indigo-800 underline col-span-2 flex items-center gap-1 font-medium">
                                                    View Terms <ExternalLink size={12} />
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {notif.status === 'PENDING' && (
                            <div className="flex gap-2 flex-wrap">
                                {/* Simple Done / Aborted for owner task or access request with no owner */}
                                {(notif.type === 'OWNER_TASK' || ((notif.type === 'ACCESS_REQUEST' || notif.type === 'TRAINING_REQUEST') && !getToolOwnerId(notif))) ? (
                                    <>
                                        <button 
                                            onClick={() => handleSimpleAction(notif, 'DONE')}
                                            className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors text-xs font-bold"
                                            title="Mark as done"
                                        >
                                            Done
                                        </button>
                                        <button 
                                            onClick={() => handleSimpleAction(notif, 'ABORTED')}
                                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg transition-colors text-xs font-bold"
                                            title="Abort"
                                        >
                                            Aborted
                                        </button>
                                    </>
                                ) : notif.type === 'TRAINING_COMPLETED' ? (
                                    <button 
                                        onClick={() => handleSimpleAction(notif, 'ACKNOWLEDGED')}
                                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg transition-colors text-xs font-bold"
                                        title="Acknowledge"
                                    >
                                        Acknowledge
                                    </button>
                                ) : (
                                    <button 
                                        onClick={() => openReviewModal(notif)}
                                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-sm text-sm font-medium flex items-center gap-2"
                                        title="Review Request"
                                    >
                                        Review <ChevronRight size={16} />
                                    </button>
                                )}
                            </div>
                        )}
                        {notif.status === 'CLARIFICATION_NEEDED' && (
                            <div className="text-xs font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-full flex items-center gap-1">
                                <MessageCircle size={14} /> Waiting for reply
                            </div>
                        )}
                    </div>
                </div>
            ))
        )}
      </div>

      {/* --- REVIEW MODAL --- */}
      {selectedNotification && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                  <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                      <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-slate-800">Review Request</h3>
                          {isAccessChangeRequest(selectedNotification) && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase bg-indigo-100 text-indigo-700">
                                  Change
                              </span>
                          )}
                      </div>
                      <button onClick={() => setSelectedNotification(null)} className="text-slate-400 hover:text-slate-600">
                          <X size={24} />
                      </button>
                  </div>
                  
                  <div className="p-6 overflow-y-auto">
                      {isAccessChangeRequest(selectedNotification) && (
                          <div className="mb-4 p-3 rounded-lg border border-indigo-100 bg-indigo-50 text-indigo-800 text-sm">
                              This is an access change request for an already-assigned tool.
                          </div>
                      )}
                      
                      {/* --- MESSAGE HISTORY --- */}
                      {selectedNotification.history && selectedNotification.history.length > 0 && (
                          <div className="mb-6 space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-100 max-h-48 overflow-y-auto">
                              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Conversation History</h4>
                              {selectedNotification.history.map((msg, idx) => (
                                  <div key={idx} className={`flex flex-col ${msg.role === 'admin' ? 'items-end' : 'items-start'}`}>
                                      <div className={`p-2 rounded-lg text-sm max-w-[80%] ${msg.role === 'admin' ? 'bg-indigo-100 text-indigo-900 rounded-br-none' : 'bg-white border border-slate-200 text-slate-700 rounded-bl-none'}`}>
                                          <span className="block text-[10px] font-bold opacity-50 mb-1">{msg.senderName} • {new Date(msg.date).toLocaleDateString()}</span>
                                          {msg.message}
                                      </div>
                                  </div>
                              ))}
                          </div>
                      )}

                      {/* --- CONTENT FOR NEW TOOL PROCUREMENT --- */}
                      {selectedNotification.type === 'NEW_TOOL_REQUEST' && (
                          <div className="space-y-6">
                              <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-lg">
                                  <h4 className="font-bold text-indigo-900 text-sm mb-2 flex items-center gap-2">
                                      <FileCheck size={16} /> Procurement Checklist
                                  </h4>
                                  <p className="text-xs text-indigo-700 mb-4">You must validate these items before approving.</p>
                                  
                                  <div className="space-y-3">
                                      <label className="flex items-center gap-3 p-2 bg-white rounded border border-indigo-100 cursor-pointer hover:border-indigo-300">
                                          <input 
                                            type="checkbox" 
                                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                                            checked={checklist.budget}
                                            onChange={(e) => setChecklist({...checklist, budget: e.target.checked})}
                                          />
                                          <span className="text-sm font-medium text-slate-700">Budget Approved</span>
                                      </label>
                                      <label className="flex items-center gap-3 p-2 bg-white rounded border border-indigo-100 cursor-pointer hover:border-indigo-300">
                                          <input 
                                            type="checkbox" 
                                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                                            checked={checklist.security}
                                            onChange={(e) => setChecklist({...checklist, security: e.target.checked})}
                                          />
                                          <span className="text-sm font-medium text-slate-700">Security & GDPR Review</span>
                                      </label>
                                      <label className="flex items-center gap-3 p-2 bg-white rounded border border-indigo-100 cursor-pointer hover:border-indigo-300">
                                          <input 
                                            type="checkbox" 
                                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                                            checked={checklist.tech}
                                            onChange={(e) => setChecklist({...checklist, tech: e.target.checked})}
                                          />
                                          <span className="text-sm font-medium text-slate-700">IT Compatibility Check</span>
                                      </label>
                                  </div>
                              </div>
                              
                              <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Response / Notes</label>
                                  <textarea 
                                      className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none h-20 resize-none"
                                      placeholder="Comments for approval, or questions for clarification..."
                                      value={adminNotes}
                                      onChange={(e) => setAdminNotes(e.target.value)}
                                  />
                              </div>

                              <div className="flex gap-3 pt-2">
                                  <button 
                                    onClick={handleRequestInfo}
                                    disabled={!adminNotes.trim()}
                                    className="flex-1 py-2.5 bg-white border border-indigo-200 text-indigo-700 rounded-lg font-medium hover:bg-indigo-50 transition-colors disabled:opacity-50 flex justify-center items-center gap-2"
                                  >
                                      <MessageCircle size={18} /> Request Info
                                  </button>
                                  <button 
                                    onClick={handleApproveProcurement}
                                    disabled={!checklist.budget || !checklist.security || !checklist.tech}
                                    className="flex-1 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                                  >
                                      <Check size={18} /> Approve
                                  </button>
                              </div>
                          </div>
                      )}

                      {/* --- CONTENT FOR OWNER TASK: simple Done / Aborted only --- */}
                      {selectedNotification.type === 'OWNER_TASK' && (
                          <div className="space-y-6">
                              <div className="bg-slate-50 border border-slate-200 p-4 rounded-lg">
                                  <p className="text-sm text-slate-700">
                                      {selectedNotification.accessReason || selectedNotification.trainingNeeds || 'No details.'}
                                  </p>
                              </div>
                              <p className="text-xs text-slate-500">Close this task without any other action.</p>
                              <div className="flex gap-3 pt-2">
                                  <button 
                                      onClick={() => { handleSimpleAction(selectedNotification, 'DONE'); setSelectedNotification(null); }}
                                      className="flex-1 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex justify-center items-center gap-2"
                                  >
                                      <Check size={18} /> Done
                                  </button>
                                  <button 
                                      onClick={() => { handleSimpleAction(selectedNotification, 'ABORTED'); setSelectedNotification(null); }}
                                      className="flex-1 py-2.5 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 transition-colors flex justify-center items-center gap-2"
                                  >
                                      <X size={18} /> Aborted
                                  </button>
                              </div>
                          </div>
                      )}

                      {/* --- CONTENT FOR ACCESS / TRAINING (FORWARDING) --- */}
                      {(selectedNotification.type === 'ACCESS_REQUEST' || selectedNotification.type === 'TRAINING_REQUEST') && (
                          <div className="space-y-6">
                              <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg">
                                  <h4 className="font-bold text-blue-900 text-sm mb-2 flex items-center gap-2">
                                      <Send size={16} /> Routing Action
                                  </h4>
                                  <p className="text-xs text-blue-700 mb-4">
                                      This request should be handled by the Tool Owner.
                                  </p>
                                  
                                  <div className="bg-white p-3 rounded border border-blue-100 flex items-center justify-between">
                                      <div>
                                          <span className="text-xs font-bold text-slate-400 uppercase">Tool Owner</span>
                                          <div className="font-bold text-slate-800">{getToolOwnerName(selectedNotification)}</div>
                                      </div>
                                      {!getToolOwnerId(selectedNotification) && (
                                          <div className="text-amber-600 text-xs font-bold flex items-center gap-1">
                                              <AlertTriangle size={12} /> No Owner
                                          </div>
                                      )}
                                  </div>
                              </div>

                              <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Response / Note to Owner</label>
                                  <textarea 
                                      className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none h-20 resize-none"
                                      placeholder="E.g. Approved by management, please proceed..."
                                      value={adminNotes}
                                      onChange={(e) => setAdminNotes(e.target.value)}
                                  />
                              </div>

                              <div className="flex gap-3 pt-2">
                                   <button 
                                    onClick={handleRequestInfo}
                                    disabled={!adminNotes.trim()}
                                    className="flex-1 py-2.5 bg-white border border-indigo-200 text-indigo-700 rounded-lg font-medium hover:bg-indigo-50 transition-colors disabled:opacity-50 flex justify-center items-center gap-2"
                                  >
                                      <MessageCircle size={18} /> Request Info
                                  </button>
                                   <button 
                                    onClick={handleValidateAndForward}
                                    disabled={!getToolOwnerId(selectedNotification)} // Can't forward if no owner
                                    className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex justify-center items-center gap-2"
                                  >
                                      <Send size={18} /> Validate & Forward
                                  </button>
                              </div>
                              {!getToolOwnerId(selectedNotification) && (
                                  <p className="text-center text-xs text-red-500 font-medium">
                                      You must assign an owner to this tool in the Inventory before forwarding.
                                  </p>
                              )}

                              <div className="pt-4 border-t border-slate-100">
                                  <p className="text-xs font-medium text-slate-500 mb-2">Or close without action</p>
                                  <div className="flex gap-2">
                                      <button 
                                          onClick={() => { handleSimpleAction(selectedNotification, 'DONE'); setSelectedNotification(null); }}
                                          className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm font-medium transition-colors"
                                      >
                                          Done
                                      </button>
                                      <button 
                                          onClick={() => { handleSimpleAction(selectedNotification, 'ABORTED'); setSelectedNotification(null); }}
                                          className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-sm font-medium transition-colors"
                                      >
                                          Aborted
                                      </button>
                                  </div>
                              </div>
                          </div>
                      )}

                      {/* --- REJECT SECTION (ALWAYS AVAILABLE) --- */}
                      <div className="mt-8 pt-6 border-t border-slate-100">
                          <details className="group">
                              <summary className="text-xs font-bold text-red-500 cursor-pointer hover:text-red-700 flex items-center gap-1">
                                  <X size={12} /> Reject Request
                              </summary>
                              <div className="mt-3">
                                  <label className="block text-xs font-bold text-slate-700 mb-1">Reason for Rejection *</label>
                                  <textarea 
                                      className="w-full border border-red-200 bg-red-50 rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-500 outline-none h-20 resize-none mb-3"
                                      placeholder="Why is this being rejected?"
                                      value={rejectReason}
                                      onChange={(e) => setRejectReason(e.target.value)}
                                  />
                                  <button 
                                    onClick={handleReject}
                                    disabled={!rejectReason.trim()}
                                    className="w-full py-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors disabled:opacity-50 text-sm"
                                  >
                                      Confirm Rejection
                                  </button>
                              </div>
                          </details>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};